import React, { useEffect, useState } from "react";
import { CircularProgress, Modal, ModalClose, ModalDialog } from "@mui/joy";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import { DeleteForever } from "@mui/icons-material";

// Import the new ApiService singleton
import ApiService from "../../axios/Bookings-Axios";

import 'react-datepicker/dist/react-datepicker.css';
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import CustomersTopComponent from "./CustomersTopComponent";

// Assuming Customer type is defined elsewhere
interface Customer {
    advertisement_platform: any;
    created_at: string;
    email: string;
    full_name: string;
    house_type: any;
    id: number;
    notes: string;
    phone: string;
    postcode: string;
    street_address: string;
}

interface CustomersModalProps {
    openState: boolean;
    customerId: number;
    parentCallback: () => void;
}

export default function CustomersModal({ openState, customerId, parentCallback }: CustomersModalProps) {
    const [open, setOpen] = useState(openState);
    const [loadedData, setLoadedData] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

    const [customer, setCustomer] = useState<Customer>({
        advertisement_platform: undefined,
        created_at: "",
        email: "",
        full_name: "",
        house_type: undefined,
        id: 0,
        notes: "",
        phone: "",
        postcode: "",
        street_address: ""
    });

    // This handler can be removed as CustomersTopComponent now handles its own state logic.
    // Kept for reference if needed.
    const handleCustomerInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setCustomer(prevCustomer => ({
            ...prevCustomer,
            [name]: value
        }));
    };

    // This handler can also be removed for the same reason.
    const handleCustomerHouseTypeChange = (newValue: string | null) => {
        if (newValue !== null) {
            setCustomer(prevCustomer => ({
                ...prevCustomer,
                house_type: { name: newValue, id: -1 }
            }));
        }
    };

    useEffect(() => {
        if (customerId === 0) {
            return;
        }
        setLoadedData(false);
        const fetchData = async () => {
            try {
                let customerData = await ApiService.getCustomerById(customerId);
                if (customerData.notes == null) {
                    customerData.notes = '';
                }
                setCustomer(customerData);
            } catch (error) {
                console.error("Failed to fetch customer:", error);
                // Optionally handle the error, e.g., show a toast message
            } finally {
                setLoadedData(true);
            }
        };
        fetchData();
    }, [customerId]); // Add customerId dependency to re-fetch if it changes


    function closeModal() {
        setOpen(false);
        parentCallback();
    }

    const handleDeleteCallBack = async (doDelete: boolean) => {
        setOpenDeleteModal(false);
        if (!doDelete) {
            return;
        }
        try {
            // REFACTORED: Use ApiService
            await ApiService.deleteCustomerById(customerId);
            window.location.reload(false);
        } catch (error) {
            console.error("Failed to delete customer:", error);
        }
    };

    const handleSave = async (event: React.FormEvent) => {
        event.preventDefault();
        try {
            await ApiService.updateCustomer(customerId, customer);
            window.location.reload(false);
        } catch (error) {
            console.error("Failed to update customer:", error);
        }
    };

    return (
        <>
            <Modal
                slotProps={{
                    backdrop: {
                        sx: {
                            backdropFilter: 'none',
                            backgroundColor: 'rgb(108 108 108 / 60%)',
                            padding: '10px'
                        },
                    },
                }}
                open={open}
                onClose={closeModal}
            >
                <ModalDialog sx={{ 
                    overflowY: 'scroll',
                    width: { xs: '95vw', sm: 'auto' },
                    maxWidth: { xs: '95vw', sm: '600px' },
                    maxHeight: '90vh',
                }}>
                    <ModalClose />
                    <Box sx={{
                        minWidth: { xs: 'auto', sm: 600 },
                        display: 'flex',
                        flexDirection: 'column',
                        overflowX: 'hidden',
                    }}>
                    </Box>

                    {!loadedData ? (
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            minWidth: '50%',
                            height: '100%',
                            justifyContent: 'center'
                        }}>
                            <CircularProgress size="lg" />
                        </Box>
                    ) : (
                        <form onSubmit={handleSave} id="a-form">
                            {/* Pass customer data and a setter function to the child component */}
                            <CustomersTopComponent customer={customer} setCustomer={setCustomer} />
                        </form>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 2 }}>
                        {loadedData && (
                            <Button
                                sx={{ marginRight: 'auto' }} // Pushes to the far left
                                color="danger"
                                endDecorator={<DeleteForever />}
                                onClick={() => setOpenDeleteModal(true)}
                            >
                                Delete
                            </Button>
                        )}
                        {loadedData && customerId !== -1 && ( // Assuming -1 might be for 'new'
                            <Button form="a-form" type="submit" sx={{ marginLeft: '10px' }}>
                                Save Customer
                            </Button>
                        )}
                        {loadedData && customerId === -1 && ( // Assuming -1 might be for 'new'
                            <Button form="a-form" type="submit" sx={{ marginLeft: '10px' }}>
                                Create New Customer
                            </Button>
                        )}
                    </Box>
                </ModalDialog>
            </Modal>
            <ConfirmDeleteModal
                discardMessage="Are you sure you want to delete this customer?"
                openState={openDeleteModal}
                parentCallback={handleDeleteCallBack}
            />
        </>
    );
}